/**
 * TableCell Component
 * 
 * Renders a table cell with appropriate styling based on content type.
 */

import React from 'react';
import { formatCellContent, processInlineMarkdown, getCellTypeClasses } from '@/shared/lib/markdown';
import type { ColumnAlignment } from '@/shared/lib/markdown';

interface TableCellProps {
    /** Raw cell content */
    content: string;
    /** Column alignment */
    alignment: ColumnAlignment;
    /** Whether this is the first cell in the row */
    isFirst?: boolean;
    /** Whether this is the last cell in the row */
    isLast?: boolean;
}

/**
 * Table cell with automatic type detection and formatting.
 * Applies semantic colors for currency, percentage, and number values.
 */
export const TableCell: React.FC<TableCellProps> = ({
    content,
    alignment,
    isFirst = false,
    isLast = false,
}) => {
    const { formatted, type } = formatCellContent(content);
    const typeClasses = getCellTypeClasses(type);

    // Determine final alignment: types override column alignment
    const finalAlignment = type !== 'text' ? 'right' : alignment;

    const alignmentClass = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
    }[finalAlignment];

    return (
        <td
            className={`
                px-5 py-4
                ${typeClasses}
                ${alignmentClass}
                ${isFirst ? 'pl-6' : ''}
                ${isLast ? 'pr-6' : ''}
            `}
            dangerouslySetInnerHTML={{ __html: processInlineMarkdown(formatted) }}
        />
    );
};
